import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { type PlanPermissions } from '../../hooks/usePlanPermissions';
import { UpgradeModal } from './UpgradeModal';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { usePinLockContext } from '../../contexts/PinLockContext';
import type { UserRole } from '../../contexts/AuthContext';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  plan: 'free' | 'paid';
  userName: string;
  avatarUrl?: string | null;
  avatarInitials?: string;
  permissions?: PlanPermissions;
}

interface NavItem {
  icon: string;
  label: string;
  path: string;
  permissionKey?: keyof PlanPermissions;
  upgradeTitle?: string;
  upgradeDescription?: string;
}

const mainNavItems: NavItem[] = [
  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/dashboard' },
  { icon: 'ri-bank-line', label: 'Contas', path: '/contas' },
  { icon: 'ri-exchange-dollar-line', label: 'Lançamentos', path: '/lancamentos' },
  { icon: 'ri-bank-card-line', label: 'Cartões', path: '/cartoes' },
  { icon: 'ri-user-heart-line', label: 'Favorecidos', path: '/favorecidos' },
  { icon: 'ri-focus-3-line', label: 'Metas', path: '/metas' },
  { icon: 'ri-calendar-2-line', label: 'Calendário', path: '/calendario' },
];

const extraNavItems: NavItem[] = [
  {
    icon: 'ri-lightbulb-line',
    label: 'Insights',
    path: '/insights',
    permissionKey: 'canAccessInsightsModule',
    upgradeTitle: 'Módulo de Insights',
    upgradeDescription: 'Acesse análises profundas, padrões financeiros, análise de metas, cartões e recomendações personalizadas.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    label: 'Relatórios',
    path: '/relatorios',
    permissionKey: 'canAccessReports',
    upgradeTitle: 'Relatórios & Análises',
    upgradeDescription: 'Acesse análises completas, gráficos de evolução, insights automáticos e muito mais.',
  },
];

const betaNavItems: NavItem[] = [
  { icon: 'ri-feedback-line',   label: 'Feedback',   path: '/feedback' },
  { icon: 'ri-lightbulb-flash-line', label: 'Sugestões', path: '/sugestoes' },
];

const ADMIN_ROLES_SET: UserRole[] = ['master_admin', 'admin', 'support', 'finance'];

export const Sidebar = ({
  isCollapsed, onToggle, mobileOpen = false, onMobileClose,
  plan, userName, avatarUrl, avatarInitials, permissions,
}: SidebarProps) => {
  const initials = avatarInitials || userName.slice(0, 2).toUpperCase() || 'U';
  const navigate = useNavigate();
  const [upgradeModal, setUpgradeModal] = useState<{ title: string; description: string } | null>(null);
  const { settings } = useSystemSettings();
  const { role } = useAuth();
  const { unreadCount } = useNotifications();
  const { isPinEnabled, lock } = usePinLockContext();

  const isAdminUser = !!role && ADMIN_ROLES_SET.includes(role);

  const logoUrl = settings.logo_small_url || settings.logo_url || 'https://public.readdy.ai/ai/img_res/651de865-f324-4bf8-91db-557422cf642b.png';
  const platformName = settings.platform_name || '';
  const appPrimary = settings.app_primary_color || '#10b981';

  const isLocked = (item: NavItem): boolean => {
    if (!item.permissionKey || !permissions) return false;
    if (permissions.loading) return false;
    return !(permissions[item.permissionKey] as boolean);
  };

  const handleNavClick = (e: React.MouseEvent, item: NavItem) => {
    if (isLocked(item)) {
      e.preventDefault();
      setUpgradeModal({
        title: item.upgradeTitle ?? item.label,
        description: item.upgradeDescription ?? `Este recurso está disponível em planos pagos.`,
      });
    } else {
      onMobileClose?.();
    }
  };

  const NavItemLink = ({ item }: { item: NavItem }) => {
    const locked = isLocked(item);
    return (
      <NavLink
        to={item.path}
        onClick={(e) => handleNavClick(e, item)}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap relative ${
            isCollapsed ? 'justify-center w-10 h-10 mx-auto' : 'px-3 py-2.5'
          } ${
            locked
              ? 'text-gray-400 hover:bg-gray-50'
              : isActive
              ? 'font-600'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`
        }
        style={({ isActive }) =>
          isActive && !locked
            ? {
                backgroundColor: `${appPrimary}18`,
                color: appPrimary,
                ...(isCollapsed ? {} : { borderLeft: `3px solid ${appPrimary}` }),
              }
            : {}
        }
        title={isCollapsed ? item.label : undefined}
      >
        {({ isActive }) => (
          <>
            <div className="w-5 h-5 flex items-center justify-center shrink-0 relative">
              <i
                className={`${item.icon} text-[18px]`}
                style={isActive && !locked ? { color: appPrimary } : undefined}
              />
              {locked && isCollapsed && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center">
                  <i className="ri-lock-fill text-white" style={{ fontSize: '7px' }} />
                </span>
              )}
            </div>
            {!isCollapsed && (
              <>
                <span className="text-[14px] flex-1">{item.label}</span>
                {locked ? (
                  <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-600 flex items-center gap-0.5">
                    <i className="ri-lock-line text-[9px]" />PRO
                  </span>
                ) : null}
              </>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full bg-white border-r border-gray-100 transition-all duration-300 z-40 flex flex-col
          ${isCollapsed ? 'w-[68px]' : 'w-[264px]'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo + Toggle */}
        <div className="flex items-center h-[72px] px-4 border-b border-gray-100 shrink-0">
          {!isCollapsed && (
            <div className="flex items-center gap-2 flex-1 overflow-hidden">
              <img
                src={logoUrl}
                alt={platformName}
                className="w-8 h-8 rounded-lg object-cover shrink-0"
              />
              <span className="text-[15px] font-700 text-gray-900 whitespace-nowrap tracking-tight">
                {platformName}
              </span>
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex justify-center">
              <img
                src={logoUrl}
                alt={platformName}
                className="w-8 h-8 rounded-lg object-cover"
              />
            </div>
          )}
          {!isCollapsed && (
            <div className="flex items-center gap-1 shrink-0">
              {/* Close mobile */}
              <button
                onClick={onMobileClose}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
              {/* Collapse desktop */}
              <button
                onClick={onToggle}
                className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
              >
                <i className="ri-layout-left-2-line text-lg" />
              </button>
            </div>
          )}
        </div>

        {isCollapsed && (
          <div className="flex justify-center py-3 border-b border-gray-100">
            <button
              onClick={onToggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
            >
              <i className="ri-layout-right-2-line text-lg" />
            </button>
          </div>
        )}

        {/* Nav - Main */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {!isCollapsed && (
            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wider px-3 mb-2">Principal</p>
          )}
          <ul className="space-y-1">
            {mainNavItems.map((item) => (
              <li key={item.path}>
                <NavItemLink item={item} />
              </li>
            ))}
          </ul>

          <div className="my-4 border-t border-gray-100" />

          {!isCollapsed && (
            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wider px-3 mb-2">Análise</p>
          )}
          <ul className="space-y-1">
            {extraNavItems.map((item) => (
              <li key={item.path}>
                <NavItemLink item={item} />
              </li>
            ))}
          </ul>

          <div className="my-4 border-t border-gray-100" />

          {!isCollapsed && (
            <div className="flex items-center gap-2 px-3 mb-2">
              <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wider">Beta</p>
              <span className="text-[9px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full font-700">NOVO</span>
            </div>
          )}
          <ul className="space-y-1">
            {betaNavItems.map((item) => (
              <li key={item.path}>
                <NavItemLink item={item} />
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom */}
        <div className="px-3 pb-4 border-t border-gray-100 pt-3 space-y-1 shrink-0">
          {!isCollapsed && (
            <div className={`rounded-xl px-3 py-2 mb-2 ${
              plan === 'paid' ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center gap-2">
                <i className={`${plan === 'paid' ? 'ri-vip-crown-line text-amber-500' : 'ri-user-line text-gray-400'} text-sm`} />
                <span className={`text-[12px] font-600 ${plan === 'paid' ? 'text-amber-700' : 'text-gray-600'}`}>
                  {permissions?.planName ?? (plan === 'paid' ? 'Plano Premium' : 'Plano Gratuito')}
                </span>
              </div>
              {plan === 'free' && (
                <button
                  onClick={() => setUpgradeModal({ title: 'Faça upgrade', description: 'Desbloqueie todos os recursos premium.' })}
                  className="text-[11px] font-500 hover:underline cursor-pointer whitespace-nowrap"
                  style={{ color: appPrimary }}
                >
                  Fazer upgrade →
                </button>
              )}
            </div>
          )}

          {/* Botão "Ir para Painel Administrativo" — visível apenas para admins */}
          {isAdminUser && !isCollapsed && (
            <button
              onClick={() => { onMobileClose?.(); navigate('/admin'); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-violet-50 border border-violet-200/60 text-violet-700 hover:bg-violet-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-shield-star-line text-[17px] text-violet-600" />
              </div>
              <span className="text-[13px] font-600">Painel Administrativo</span>
              <i className="ri-arrow-right-s-line ml-auto text-violet-400 text-[16px]" />
            </button>
          )}

          {isAdminUser && isCollapsed && (
            <button
              onClick={() => navigate('/admin')}
              title="Painel Administrativo"
              className="flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap text-violet-600 hover:bg-violet-50 justify-center w-10 h-10 mx-auto"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-shield-star-line text-[18px]" />
              </div>
            </button>
          )}

          {/* Notificações — desktop expanded */}
          {!isCollapsed && (
            <NavLink
              to="/notificacoes"
              onClick={onMobileClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'font-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      backgroundColor: `${appPrimary}18`,
                      color: appPrimary,
                      borderLeft: `3px solid ${appPrimary}`,
                    }
                  : {}
              }
            >
              {({ isActive }) => (
                <>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i
                      className="ri-notification-3-line text-[18px]"
                      style={isActive ? { color: appPrimary } : undefined}
                    />
                  </div>
                  <span className="text-[14px] flex-1">Notificações</span>
                  {unreadCount > 0 && (
                    <span className="min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-700 rounded-full px-1">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )}

          {/* Notificações — collapsed */}
          {isCollapsed && (
            <NavLink
              to="/notificacoes"
              title="Notificações"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap justify-center w-10 h-10 mx-auto relative ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-600 border-l-[3px] border-emerald-500 font-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              {({ isActive }) => (
                <div className="w-5 h-5 flex items-center justify-center shrink-0 relative">
                  <i className={`ri-notification-3-line text-[18px] ${isActive ? 'text-emerald-500' : ''}`} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center bg-red-500 rounded-full text-[8px] font-700 text-white px-0.5">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          )}

          {/* Bloquear agora — expanded */}
          {isPinEnabled && !isCollapsed && (
            <button
              onClick={() => { onMobileClose?.(); lock(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-lock-line text-[18px]" />
              </div>
              <span className="text-[14px] flex-1 text-left">Bloquear agora</span>
            </button>
          )}

          {/* Bloquear agora — collapsed */}
          {isPinEnabled && isCollapsed && (
            <button
              onClick={lock}
              title="Bloquear agora"
              className="flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap text-red-400 hover:bg-red-50 hover:text-red-500 justify-center w-10 h-10 mx-auto"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-lock-line text-[18px]" />
              </div>
            </button>
          )}

          {!isCollapsed && (
            <NavLink
              to="/perfil"
              onClick={onMobileClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
            >              {avatarUrl ? (
                <img src={avatarUrl} alt={userName} className="w-6 h-6 rounded-full object-cover object-top shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-700 shrink-0">
                  {initials}
                </div>
              )}
              <span className="text-[14px] truncate">{userName}</span>
            </NavLink>
          )}

          {isCollapsed && (
            <NavLink
              to="/perfil"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl transition-all duration-150 cursor-pointer whitespace-nowrap justify-center w-10 h-10 mx-auto ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-600 border-l-[3px] border-emerald-500 font-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
              title="Perfil"
            >
              {({ isActive }) => (
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`ri-user-settings-line text-[18px] ${isActive ? 'text-emerald-500' : ''}`} />
                </div>
              )}
            </NavLink>
          )}
        </div>
      </aside>

      <UpgradeModal
        open={!!upgradeModal}
        onClose={() => setUpgradeModal(null)}
        title={upgradeModal?.title}
        description={upgradeModal?.description}
      />
    </>
  );
};
